import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '../lib/supabase';
import { LogOut } from 'lucide-react';
import PageTitle from '../components/PageTitle';

// Import ShadCN UI components
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Initialize Stripe
if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
  throw new Error('Missing Stripe public key');
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

export default function Account() {
  const { user, signOut } = useAuth();
  const { handleManageSubscription } = useSubscription();
  const [subscription, setSubscription] = useState<any>(null);
  const [coverLetterCount, setCoverLetterCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchSubscription();
      fetchCoverLetterCount();
    }
  }, [user]);





  const fetchSubscription = async () => {

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();


      
      if (error && error.code !== 'PGRST116') throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchCoverLetterCount = async () => {
    if (!user?.id) return;
    
    try {
      const { count, error } = await supabase
        .from('cover_letters')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching cover letter count:', error);
        return;
      }
      
      setCoverLetterCount(count || 0);
    } catch (error) {
      console.error('Error fetching cover letter count:', error);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const session = await supabase.auth.getSession();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/account/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete account');
      }

      setSuccess('Account successfully deleted');
      // Sign out and redirect to home after short delay
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Failed to delete account');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  };

  return (
    <div className="container py-8 max-w-4xl mx-auto space-y-6">
      <PageTitle title="Account" />
      <div className="flex flex-wrap items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Account</h1>
        <Button 
          variant="outline" 
          onClick={handleSignOut}
          className="flex items-center gap-2 h-10"
        >
          <LogOut size={16} />
          <span>Sign Out</span>
        </Button>
      </div>
      
      <Card className="w-full shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Subscription Status</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="mb-4">
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}
          
          <div>
            {subscription ? (
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-6">
                  <Badge className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 text-sm font-medium transition-colors duration-200">Pro Plan Active</Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{coverLetterCount}</span>
                    <span className="text-sm text-muted-foreground">Cover Letters Generated</span>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={() => handleManageSubscription()}
                  disabled={loading}
                  className="hover:bg-slate-100 hover:text-black dark:hover:text-black transition-colors duration-200 h-10"
                >
                  <span className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
                    View Payment History
                  </span>
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <div>
                  <p>No Active Subscription</p>
                </div>
                
                <Button 
                  onClick={() => handleManageSubscription()}
                  disabled={loading}
                  className="bg-[#CAF84C] text-black hover:bg-[#b9e93a] h-10 border-none shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Subscribe Now
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="w-full shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Support</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-sm text-muted-foreground">Our support team is ready to help with any questions</p>
            </div>
            <Button 
              variant="outline" 
              className="hover:bg-slate-100 hover:text-black dark:hover:text-black transition-colors duration-200 h-10"
              onClick={() => window.open(`mailto:hello@athenlabs.com?subject=Quick Cover Letter Support&body=Hello Support Team,%0A%0AI need assistance with the following:%0A%0A`, '_blank')}
            >
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></svg>
                Contact Support
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="w-full shadow-sm border-destructive/50">
        <CardHeader className="pb-0">
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription className="mb-2">
            Permanent action that cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-end">
          <Button 
            variant="destructive" 
            onClick={() => setDeleteDialogOpen(true)}
            className="sm:w-auto h-10"
          >
            Delete Account
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete your account and all related data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount} 
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
